import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Briefcase, Users, Code2, Newspaper, Mail, type LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/Command';
import { usePosts, useProjects, useServices } from '@/lib/cms';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATIC_PAGES: Array<{ label: string; href: string; icon: LucideIcon; shortcut?: string }> = [
  { label: 'Home', href: '/', icon: Icons.Home },
  { label: 'Projects', href: '/projects', icon: Briefcase },
  { label: 'Services', href: '/services', icon: Code2 },
  { label: 'Blog', href: '/blog', icon: Newspaper },
  { label: 'Careers', href: '/careers', icon: Users },
  { label: 'About', href: '/about', icon: Icons.Building2 },
  { label: 'Contact', href: '/contact', icon: Mail },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();
  const { data: posts = [] } = usePosts();
  const { data: services = [] } = useServices();

  const projectsGroup = useMemo(
    () => projects.slice(0, 10).map((p) => ({ id: p.id, label: p.title, href: `/projects/${p.slug}`, icon: Briefcase as LucideIcon })),
    [projects],
  );
  const postsGroup = useMemo(
    () => posts.slice(0, 10).map((p) => ({ id: p.id, label: p.title, href: `/blog/${p.slug}`, icon: FileText as LucideIcon })),
    [posts],
  );
  const servicesGroup = useMemo(
    () => services.map((s) => ({ id: s.id, label: s.title, href: `/services#${s.slug}`, icon: Code2 as LucideIcon })),
    [services],
  );

  const go = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages, projects, posts…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {STATIC_PAGES.map((p) => (
            <CommandItem key={p.href} value={p.label} onSelect={() => go(p.href)}>
              <p.icon className="h-4 w-4 text-fg-muted" />
              <span>{p.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {servicesGroup.length > 0 && (
          <CommandGroup heading="Services">
            {servicesGroup.map((s) => (
              <CommandItem key={s.id} value={s.label} onSelect={() => go(s.href)}>
                <s.icon className="h-4 w-4 text-fg-muted" />
                <span>{s.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {projectsGroup.length > 0 && (
          <CommandGroup heading="Projects">
            {projectsGroup.map((p) => (
              <CommandItem key={p.id} value={p.label} onSelect={() => go(p.href)}>
                <p.icon className="h-4 w-4 text-fg-muted" />
                <span>{p.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {postsGroup.length > 0 && (
          <CommandGroup heading="Blog posts">
            {postsGroup.map((p) => (
              <CommandItem key={p.id} value={p.label} onSelect={() => go(p.href)}>
                <p.icon className="h-4 w-4 text-fg-muted" />
                <span>{p.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Tip">
          <CommandItem disabled value="tip">
            <Search className="h-4 w-4 text-fg-muted" />
            <span className="text-fg-muted">Press</span>
            <CommandShortcut>⌘K</CommandShortcut>
            <span className="text-fg-muted">anywhere to open this</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/** Hook that wires ⌘K / Ctrl-K to open the palette. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}